'use client'

import { useCallback, useMemo } from 'react'

import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import { plainToInstance } from 'class-transformer'

import { CreationDetail } from '@/lib/api'
import { EXPLORE_LIST_KEY, FILTER_EXPLORE_LIST_KEY } from '@/lib/api/hooks/explore/use-explore'
import { USER_WORK_LIST, USER_SHARED_LIST } from '@/lib/api/hooks/user/use-user-work'

/** 无限查询缓存数据的页面类型 */
type InfiniteQueryPage = ListResponse<CreationDetail>

/** 无限查询缓存数据类型 */
type InfiniteQueryData = InfiniteData<InfiniteQueryPage, number>

/** 数据源配置 */
interface DataSource {
  data: InfiniteQueryData | undefined
  name: string
}

/**
 * 将缓存数据转换为 CreationDetail 实例
 * explore 数据在缓存中是原始数据，需要转换
 * userWork/sharedList 数据在 queryFn 中已转换，instanceof 检查可避免重复转换
 */
const transformToCreationDetail = (item: Partial<CreationDetail>): CreationDetail => {
  if (item instanceof CreationDetail) {
    return item
  }
  return plainToInstance(CreationDetail, item)
}

/**
 * 从 InfiniteQueryData 中提取并转换所有 items
 */
const extractItemsFromPages = (data: InfiniteQueryData): CreationDetail[] => {
  return data.pages.flatMap(page => (page.list || []).map(transformToCreationDetail))
}

/**
 * 检查 item 是否匹配指定的 id
 */
const isItemMatchId = (item: CreationDetail, id: string): boolean => {
  return item.jobId === id || item.id.toString() === id
}

/**
 * 用于work页面swiper的数据管理hook
 */
export const useWorkSwiper = (currentId?: string) => {
  const queryClient = useQueryClient()

  // 从多个数据源缓存中获取数据
  const { list, initialIndex } = useMemo(() => {
    // 遍历所有查询缓存以匹配 explore 列表（pageSize 可能不同）
    const queryCache = queryClient.getQueryCache()
    const allExplorData: InfiniteQueryData[] = []
    const allFilterData: InfiniteQueryData[] = []
    const allSharedListData: InfiniteQueryData[] = []
    queryCache.getAll().forEach(query => {
      const queryKey = query.queryKey
      if (Array.isArray(queryKey)) {
        // 匹配任意 pageSize 的 EXPLORE_LIST_KEY
        if (queryKey[0] === EXPLORE_LIST_KEY) {
          const data = queryClient.getQueryData<InfiniteQueryData>(queryKey)
          if (data) {
            allExplorData.push(data)
          }
        }
        // 匹配 FILTER_EXPLORE_LIST_KEY
        if (queryKey[0] === FILTER_EXPLORE_LIST_KEY) {
          const data = queryClient.getQueryData<InfiniteQueryData>(queryKey)
          if (data) {
            allFilterData.push(data)
          }
        }
        // 匹配 USER_SHARED_LIST
        if (queryKey[0] === USER_SHARED_LIST && queryKey.length === 2) {
          const data = queryClient.getQueryData<InfiniteQueryData>(queryKey)
          if (data) {
            allSharedListData.push(data)
          }
        }
      }
    })

    // 尝试从用户作品列表缓存中获取数据
    const userWorkData = queryClient.getQueryData<InfiniteQueryData>([USER_WORK_LIST, true])
    const userWorkDataFalse = queryClient.getQueryData<InfiniteQueryData>([USER_WORK_LIST, false])

    let allItems: CreationDetail[] = []
    let sourceWithCurrentId: CreationDetail[] | null = null

    // 收集所有数据源
    const dataSources: DataSource[] = [
      ...allExplorData.map((data, index) => ({ data, name: `explore_${index}` })),
      ...allFilterData.map((data, index) => ({ data, name: `filter_${index}` })),
      { data: userWorkData, name: 'userWork' },
      { data: userWorkDataFalse, name: 'userWorkFalse' },
      ...allSharedListData.map((data, index) => ({ data, name: `sharedList_${index}` }))
    ]

    // 首先查找包含当前ID的数据源（先转换再查找，统一使用转换后的字段名）
    if (currentId) {
      for (const source of dataSources) {
        if (source.data?.pages) {
          const items = extractItemsFromPages(source.data)
          const hasCurrentId = items.some(item => isItemMatchId(item, currentId))
          if (hasCurrentId) {
            sourceWithCurrentId = items
            break
          }
        }
      }
    }

    // 如果找到了包含当前ID的数据源，优先使用它
    if (sourceWithCurrentId) {
      allItems = sourceWithCurrentId
    } else {
      // 否则合并所有可用的数据源
      for (const source of dataSources) {
        if (source.data?.pages) {
          const items = extractItemsFromPages(source.data)
          // 去重：基于jobId或id
          items.forEach(item => {
            const exists = allItems.some(
              existing => existing.jobId === item.jobId || existing.id === item.id
            )
            if (!exists) {
              allItems.push(item)
            }
          })
        }
      }
    }

    // 查找当前视频的索引
    let index = 0
    if (currentId && allItems.length > 0) {
      const foundIndex = allItems.findIndex(item => isItemMatchId(item, currentId))
      if (foundIndex !== -1) {
        index = foundIndex
      }
    }

    return {
      list: allItems,
      initialIndex: index
    }
  }, [currentId, queryClient])

  // 获取指定索引的视频详情
  const getItemByIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= list.length) return null
      return list[index]
    },
    [list]
  )

  return {
    list,
    initialIndex,
    getItemByIndex,
    hasData: list.length > 0
  }
}
